import { useEffect, useState } from 'react';

/**
 * The value, but only once it has stopped changing for `delayMs`.
 *
 * For text the user is still typing. "reforma" is seven keystrokes, and without this it is seven
 * requests, six of which are for a prefix nobody asked about — the last one is the question. The
 * screen churns through six sets of wrong answers on the way to the right one, which reads as the
 * filter being broken rather than fast.
 *
 * Only free text needs this. A select and a toggle are discrete choices already: the reader has
 * finished deciding by the time the event fires, and delaying those would make the interface feel
 * unresponsive for no benefit.
 */
export function useDebouncedValue<T>(value: T, delayMs = 300): T {
  const [settled, setSettled] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), delayMs);
    // Cleanup runs on every change, so the timer only ever fires after a real pause. Without it
    // each keystroke would schedule its own update and they would all still arrive.
    return () => clearTimeout(timer);
  }, [value, delayMs]);

  return settled;
}
